/**
 * Engine 26 — Structured Psychosocial Response Layer (UI)
 * =======================================================
 * Renders inline within ClinicalOutput when result.psychosocialOffer.eligible.
 *
 * Flow:
 *   1. Consent gate (Agree / Decline)
 *   2. If Agree: show 3 pre-approved cards, each offering ONE action
 *   3. Patient picks ONE → POST /api/psychosocial/action → session ends
 *   4. If Decline: POST action='declined_consent' → session ends
 *
 * Strict UI invariants:
 *   - No free-text input, ever.
 *   - No LLM call.
 *   - Hard cap: 3 cards rendered. No "next" or "more" affordance.
 *   - Once an action is chosen, the component freezes and shows a thank-you.
 *
 * All copy is sourced from offer.cards (server-provided, clinician-reviewed scripts).
 */
import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PsychosocialSession({ encounterId, offer }) {
  const [step, setStep] = useState('consent'); // consent | cards | recorded | declined
  const [chosenAction, setChosenAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (!offer || !offer.eligible) return null;

  const postAction = async (action, consent) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/psychosocial/action`, {
        encounterId,
        sessionId: offer.sessionId,
        action,
        consentGiven: !!consent,
      });
      setChosenAction(action);
      setStep(action === 'declined_consent' ? 'declined' : 'recorded');
    } catch (e) {
      // Fail open visually but log; never block clinical flow
      setStep('recorded');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Already-recorded suppression (server returned eligible:false reason already_recorded) ──
  if (offer.reason === 'already_recorded') return null;

  // ── CONSENT GATE ──
  if (step === 'consent') {
    return (
      <div
        className="mt-4 rounded-xl p-4 border-2 border-indigo-300 bg-white"
        data-testid="psychosocial-consent"
      >
        <h4 className="font-bold text-sm text-indigo-900 mb-2">
          A brief, structured check-in
        </h4>
        <p className="text-sm text-slate-700 leading-relaxed mb-3">
          {offer.consentText}
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => setStep('cards')}
            disabled={submitting}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-md"
            data-testid="psychosocial-consent-agree"
          >
            {offer.consentButtonAgree}
          </Button>
          <Button
            onClick={() => postAction('declined_consent', false)}
            disabled={submitting}
            variant="outline"
            className="rounded-md"
            data-testid="psychosocial-consent-decline"
          >
            {offer.consentButtonDecline}
          </Button>
        </div>
        <p className="text-[10px] italic text-slate-500 mt-3">
          Engine 26 · Deterministic structured-card session · No AI conversation. Maximum {offer.maxCards} cards per session.
        </p>
      </div>
    );
  }

  // ── CARDS — patient picks ONE ──
  if (step === 'cards') {
    return (
      <div
        className="mt-4 space-y-3"
        data-testid="psychosocial-cards"
      >
        <div className="text-xs italic text-slate-600">
          Choose one of the options below. You can choose to close at any time.
        </div>
        {offer.cards.slice(0, offer.maxCards).map((card) => (
          <div
            key={card.id}
            className="rounded-xl p-4 border-2 border-indigo-200 bg-white"
            data-testid={`psychosocial-card-${card.action}`}
          >
            <h5 className="font-bold text-sm text-indigo-900 mb-2">{card.title}</h5>
            <p className="text-sm text-slate-800 leading-relaxed mb-3">{card.body}</p>
            <Button
              onClick={() => postAction(card.action, true)}
              disabled={submitting}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md"
              data-testid={`psychosocial-card-${card.action}-button`}
            >
              {card.buttonLabel}
            </Button>
            {card.attribution && (
              <p className="text-[10px] italic text-slate-500 mt-2">{card.attribution}</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  // ── RECORDED / DECLINED ──
  if (step === 'recorded') {
    const msg = chosenAction === 'connect_care_team'
      ? 'Your care team has been flagged to follow up with you.'
      : chosenAction === 'increase_checkin'
      ? 'Your care team will set up more frequent check-ins.'
      : 'Thank you for taking this moment. Support remains available in your health summary.';
    return (
      <div
        className="mt-4 rounded-xl p-4 border-2 border-emerald-300 bg-emerald-50"
        data-testid="psychosocial-recorded"
      >
        <h4 className="font-bold text-sm text-emerald-900 mb-1">Recorded</h4>
        <p className="text-sm text-slate-700 leading-relaxed">{msg}</p>
      </div>
    );
  }

  if (step === 'declined') {
    return (
      <div
        className="mt-4 rounded-xl p-3 border-2 border-slate-200 bg-slate-50"
        data-testid="psychosocial-declined"
      >
        <p className="text-sm text-slate-600">
          Thank you. Support resources remain available in your health summary whenever you are ready.
        </p>
      </div>
    );
  }

  return null;
}
